"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fetchUserAppkeys, updateAppkey } from "@/lib/tokenApi";
import logger from "@/lib/logger";
import type { Group } from "@/components/GroupSidebar";
const QUICK_AMOUNT = [5, 10, 20, 50, 100, 200, 500];

const STORAGE_KEY = "token-groups";
const QUICK_EXPIRE = [
  { label: "1天", value: 1 },
  { label: "7天", value: 7 },
  { label: "30天", value: 30 },
  { label: "90天", value: 90 },
  { label: "180天", value: 180 },
  { label: "365天", value: 365 },
];

interface Token {
  id: string;
  name: string;
  group: string;
  groupId: string;
  billing?: string;
  billing_info?: string;
  status: string;
  quota: number | string;
  used: number | string;
  model: string;
  created: string;
  limit: string;
  ip_whitelist?: string[];
  numericId?: number;
}

interface EditTokenModalProps {
  open: boolean;
  onClose: (open: boolean) => void;
  onSuccess?: (updatedToken: Token) => void;
  token: Token;
}

export default function EditTokenModal({ open, onClose, onSuccess, token }: EditTokenModalProps) {
  const [tokenName, setTokenName] = useState("");
  const [unlimit, setUnlimit] = useState(true);
  const [forever, setForever] = useState(true);
  const [enableAdvanced, setEnableAdvanced] = useState(false);
  const [amount, setAmount] = useState<number | string>("");
  const [expireTime, setExpireTime] = useState("");
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [availableModels, setAvailableModels] = useState<{ model_name: string }[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [ipWhitelist, setIpWhitelist] = useState("");
  const [loading, setLoading] = useState(false);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);

  useEffect(() => {
    if (token && open) {
      setTokenName(token.name || "");
      setUnlimit(token.quota === "无限制" || token.quota === "长期有效");
      let quotaAmount: number | string = "";
      if (typeof token.quota === "number" && isFinite(token.quota)) {
        const num = token.quota / 500000;
        quotaAmount = Number.isInteger(num) ? String(num) : Number(num.toFixed(6));
      } else if (typeof token.quota === "string") {
        if (!token.quota.includes("无限制") && !token.quota.includes("长期有效")) {
          quotaAmount = token.quota;
        }
      }
      setAmount(quotaAmount);
      const isForever = !token.limit || token.limit === "永不过期" || token.limit === "长期有效";
      setForever(isForever);
      setExpireTime(isForever ? "" : toDatetimeLocal(token.limit));
      setSelectedModels(
        token.model && token.model !== "全部模型"
          ? Array.from(new Set(token.model.split(",").map(m => m.trim())))
          : []
      );
      setIpWhitelist(token.ip_whitelist ? token.ip_whitelist.join(", ") : "");
    }
  }, [token, open]);

  const handleUpdate = async () => {
    if (!tokenName.trim()) {
      toast.error("请输入令牌名称");
      return;
    }

    if (!unlimit && (!amount || Number(amount) <= 0)) {
      toast.error("请输入有效的授权额度");
      return;
    }

    if (!forever && !expireTime) {
      toast.error("请选择过期时间");
      return;
    }

    try {
      setLoading(true);

      // 校验同分组下令牌名称不能重复
      const checkGroupId = token.groupId || token.group;
      if (checkGroupId) {
        try {
          const existingTokens = await fetchUserAppkeys(checkGroupId);
          const trimmedName = tokenName.trim();
          const isDuplicate = (existingTokens || []).some((t: unknown) => {
            if (!t || typeof t !== "object") return false;
            const rec = t as Record<string, unknown>;
            const name = typeof rec.name === "string" ? rec.name.trim() : "";
            // 排除自身
            const tokenKey = rec.key ?? rec.appkey ?? rec.id;
            if (tokenKey === token.id) return false;
            return name === trimmedName;
          });
          if (isDuplicate) {
            toast.error("当前分组下已存在同名令牌，请使用其他名称");
            setLoading(false);
            return;
          }
        } catch (err) {
          logger.warn("校验令牌名称重复失败，跳过检查:", err);
        }
      }

      const ipArray = ipWhitelist
        .split(/[,，\n]/)
        .map(s => s.trim())
        .filter(Boolean);

      // 调用 API 更新令牌（不要带上 alive 字段，避免后端把其它字段忽略）
      const expireAtStr =
        !forever && expireTime
          ? expireTime.includes("T")
            ? expireTime.replace("T", " ") + ":00"
            : expireTime
          : null;

      await updateAppkey(token.id, {
        name: tokenName.trim(),
        ip_whitelist: ipArray.length > 0 ? ipArray : undefined,
        models: selectedModels.length > 0 ? selectedModels : undefined,
        expire_at: expireAtStr,
        quota_limit: unlimit ? undefined : Number(amount) * 500000,
        quota_enabled: !unlimit,
        numericId: token.numericId,
      });

      const updatedToken: Token = {
        ...token,
        id: token.id,
        name: tokenName.trim(),
        group: token.group,
        groupId: token.groupId,
        billing: unlimit ? "不限额" : `限额 ${amount}`,
        billing_info: unlimit ? "不限额" : `限额 ${amount}`,
        quota: unlimit ? "无限制" : `${Number(amount).toFixed(2)}`,
        used: token.used,
        model: selectedModels.length > 0 ? selectedModels.join(", ") : "全部模型",
        status: token.status,
        created: token.created,
        limit: forever ? "长期有效" : expireTime || token.limit,
        ip_whitelist: ipWhitelist
          .split(/[,，\n]/)
          .map(s => s.trim())
          .filter(Boolean),
      };

      toast.success("令牌已更新");
      resetForm();
      onClose(false);
      onSuccess && onSuccess(updatedToken);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("更新令牌失败:", { error: msg });
      toast.error("更新令牌失败: " + msg);
    } finally {
      setLoading(false);
    }
  };

  const toDatetimeLocal = (s?: string | null) => {
    if (!s) return "";
    // Accept formats: "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD HH:mm:ss", or other parseable strings
    try {
      if (s.includes("T")) return s.slice(0, 16);
      if (s.includes(" ")) return s.replace(" ", "T").slice(0, 16);
      const d = new Date(s);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 16);
    } catch {
      // ignore
    }
    return "";
  };

  const resetForm = () => {
    setTokenName("");
    setUnlimit(true);
    setForever(true);
    setEnableAdvanced(false);
    setAmount("");
    setExpireTime("");
    setSelectedModels([]);
    setIpWhitelist("");
    setModelMenuOpen(false);
  };

  const handleClose = () => {
    resetForm();
    onClose(false);
  };

  const toggleModel = (value: string) => {
    setSelectedModels(prev =>
      prev.includes(value) ? prev.filter(m => m !== value) : [...prev, value]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-left">编辑令牌</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 p-1">
          <div className="space-y-2">
            <Label>令牌名称 *</Label>
            <Input
              placeholder="请输入令牌名称"
              value={tokenName}
              onChange={e => setTokenName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>所属分组</Label>
            <div className="px-3 py-2 rounded-md border border-input bg-muted text-muted-foreground">
              {token.group || "未知分组"}
            </div>
            <p className="text-xs text-muted-foreground">令牌不可修改所属分组</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={unlimit} onCheckedChange={setUnlimit} />
              <Label>无限额度</Label>
            </div>

            {!unlimit && (
              <div className="space-y-3 ml-8">
                <Label>授权额度（美元）*</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="请输入额度"
                />

                {/* 快捷额度 */}
                <div className="flex flex-wrap gap-2">
                  {QUICK_AMOUNT.map(a => (
                    <Button
                      key={a}
                      variant={amount == a ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAmount(a)}
                    >
                      {a}$
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 永不过期 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={forever} onCheckedChange={setForever} />
              <Label>永不过期</Label>
            </div>

            {/* 永不过期关闭 → 显示过期时间 */}
            {!forever && (
              <div className="space-y-3 ml-8">
                <Label>过期时间 *</Label>
                <Input
                  type="datetime-local"
                  value={expireTime}
                  onChange={e => setExpireTime(e.target.value)}
                />

                {/* 快捷过期选项 */}
                <div className="flex flex-wrap gap-2">
                  {QUICK_EXPIRE.map(opt => (
                    <Button
                      key={opt.value}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const d = new Date();
                        d.setDate(d.getDate() + opt.value);
                        setExpireTime(d.toISOString().slice(0, 16));
                      }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 启用高级选项 */}
          <div className="flex items-center gap-2">
            <Switch checked={enableAdvanced} onCheckedChange={setEnableAdvanced} />
            <Label>启用高级选项</Label>
          </div>

          {/* 高级选项区域（可折叠） */}
          {enableAdvanced && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted">
              <div className="space-y-2">
                <Label>可用模型</Label>
                <DropdownMenu
                  open={modelMenuOpen}
                  onOpenChange={open => {
                    setModelMenuOpen(open);
                    if (open && availableModels.length === 0) {
                      setLoadingModels(true);
                      fetch("/api/pricing/models")
                        .then(res => res.json())
                        .then((raw: any) => {
                          // 支持三种返回结构：[] | { data: [] } | { models: [] }
                          const src = Array.isArray(raw)
                            ? raw
                            : raw && Array.isArray(raw.data)
                            ? raw.data
                            : raw && Array.isArray(raw.models)
                            ? raw.models
                            : [];

                          const mapped = src.map((item: any) => ({
                            model_name: item?.model_name ?? item?.name ?? String(item ?? ""),
                          }));
                          setAvailableModels(mapped);
                        })
                        .catch(err => {
                          logger.error("/api/pricing/models fetch failed", err);
                          setAvailableModels([]);
                        })
                        .finally(() => setLoadingModels(false));
                    }
                  }}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full min-h-[42px] justify-start text-left"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        {selectedModels.map(model => {
                          const modelLabel = model;
                          return (
                            <span
                              key={model}
                              role="button"
                              tabIndex={0}
                              title={`取消选中 ${modelLabel}`}
                              aria-label={`取消选中 ${modelLabel}`}
                              className="inline-flex items-center gap-1 border border-primary/30 bg-primary/10 px-2 py-1 text-xs text-primary max-w-[160px] truncate cursor-pointer"
                              onClick={e => e.stopPropagation()}
                            >
                              <span className="truncate block max-w-[140px]">{modelLabel}</span>
                            </span>
                          );
                        })}

                        {selectedModels.length === 0 && (
                          <span className="text-sm text-muted-foreground">选择模型（可多选）</span>
                        )}
                      </div>
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="start"
                    side="bottom"
                    sideOffset={4}
                    avoidCollisions={false}
                    className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-[200px] overflow-y-auto p-0"
                  >
                    {loadingModels ? (
                      <div className="p-3 text-sm text-muted-foreground">加载中...</div>
                    ) : availableModels.length > 0 ? (
                      availableModels.map(opt => {
                        const active = selectedModels.includes(opt.model_name);

                        return (
                          <DropdownMenuItem
                            key={opt.model_name}
                            className={cn(
                              "flex items-center justify-between px-3 py-2 text-sm cursor-pointer rounded-none",
                              active
                                ? "bg-primary/10 text-primary"
                                : "hover:bg-secondary"
                            )}
                            onSelect={event => {
                              event.preventDefault();
                              toggleModel(opt.model_name);
                            }}
                          >
                            <span className="truncate max-w-[220px] block">{opt.model_name}</span>
                            {active && (
                              <Check size={16} className="text-primary" />
                            )}
                          </DropdownMenuItem>
                        );
                      })
                    ) : (
                      <div className="p-3 text-sm text-muted-foreground">暂无可用模型</div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div>
                <Label className="mb-2">IP 白名单</Label>
                <Input
                  placeholder="例如：1.2.3.4, 5.6.7.8"
                  value={ipWhitelist}
                  onChange={e => setIpWhitelist(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* 提交按钮 */}
          <div className="flex gap-3 pt-3">
            <Button
              onClick={handleUpdate}
              disabled={loading}
              className="flex-1"
            >
              {loading ? "保存中..." : "保存"}
            </Button>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              取消
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
